<template>
    <!--
      진짜 재귀 컴포넌트. 자기 자신을 부르므로 깊이 제한이 없다.
      기존 렌더러는 `structure[i].element_contents[j].structure[k]` 처럼 경로에
      깊이를 문자열로 박아 3단계까지만 다뤘고, 실데이터의 55% 가 이미 그걸 넘겼다.
    -->

    <!-- 화면에 아무것도 안 그리는 노드: 미리보기에서도 보여야 편집할 수 있다 -->
    <div v-if="invisible" class="sv2-phantom" :class="{sel: isSel, struct: showStruct}" @click.stop="$emit('pick', node.id)">
        <span class="mono">{{ node.type }}</span>
        <span>내용이 비어 협력사 화면에 안 나타납니다</span>
    </div>

    <!-- 표: 실제 격자로 그린다. 헤더도 칸도 각각 노드다. -->
    <div v-else-if="node.type === 'table'" class="sv2-nd" :class="{sel: isSel, struct: showStruct}" @click.stop="$emit('pick', node.id)">
        <span class="chip">{{ node.type }}</span>
        <div class="tw">
            <table class="pt">
                <thead>
                    <tr>
                        <th v-for="(col, ci) in node.columns" :key="col.id" class="colh"
                            :class="{sel: col.id === sel, drop: dragOver === ci}"
                            :data-mode="ko(col.type)"
                            :title="'클릭해서 편집 · 끌어서 순서 변경'"
                            draggable="true"
                            @click.stop="$emit('pick', col.id)"
                            @dragstart="onDragStart(ci, $event)"
                            @dragover.prevent="dragOver = ci"
                            @dragleave="dragOver === ci && (dragOver = -1)"
                            @drop.prevent="onDrop(node.id, ci)"
                            @dragend="dragFrom = -1; dragOver = -1">
                            <span class="grip">⠿</span>{{ col.label || '(이름 없음)' }}</th>
                        <th class="colh add" title="열 추가"
                            @click.stop="$emit('add-column', node.id)">+</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in node.children" :key="row.id" :class="{mkrow: !!mk(row)}" :style="mkStyle(row)">
                        <td v-for="cell in row.children" :key="cell.id" class="cell"
                            :class="{sel: cellSel(cell), mk: !!mk(content(cell))}" :style="mkStyle(content(cell))"
                            :data-mode="ko(contentType(cell))"
                            @click.stop="$emit('pick', (content(cell) || cell).id)">
                            <span v-if="mk(content(cell))" class="dots">
                                <span v-for="m in mk(content(cell))" :key="m.kind + m.label" :class="m.kind === 'evidence' ? 'clip' : 'dot'"
                                      :style="{'--c': m.c, '--ct': m.ct}" :title="(m.kind === 'evidence' ? '증빙 위치 · 평가요소 ' : '조건 ') + m.label"><AppIcon v-if="m.kind === 'evidence'" name="paperclip"/>{{ m.label }}</span>
                            </span>
                            <span v-if="!content(cell)" class="inp empty">(비어 있음)</span>
                            <span v-else-if="content(cell).type === 'plain_text'" class="disp">{{ content(cell).label }}</span>
                            <!-- 응답자 화면은 '고른 값' 을 보여준다. 안 골랐으면 비어 있다.
                                 첫 선택지를 대신 그리면 기본값이 있는 것처럼 보인다. -->
                            <span v-else-if="content(cell).type === 'dropdown'" class="inp dd"
                                  :class="{req: content(cell).required, empty: !content(cell).children.length}">
                                <template v-if="!content(cell).children.length">(선택지 없음)</template>
                                <template v-else>{{ content(cell).srcValue == null ? '' : content(cell).srcValue }}</template>
                            </span>
                            <span v-else class="inp" :class="{req: content(cell).required}">{{ content(cell).placeholder || '' }}</span>
                        </td>
                        <td class="filler">
                            <span v-if="mk(row)" class="dots rowdots"><span v-for="m in mk(row)" :key="m.kind + m.label" class="clip" :style="{'--c': m.c, '--ct': m.ct}" :title="'증빙 위치 · 평가요소 ' + m.label"><AppIcon name="paperclip"/>{{ m.label }}</span></span>
                        </td>
                    </tr>
                    <tr>
                        <td class="addrow" :colspan="Math.max(1, (node.columns || []).length)" title="행 추가"
                            @click.stop="$emit('add-row', node.id)">+</td>
                        <td class="filler"></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <p v-if="node.orphanKeys && node.orphanKeys.length" class="orph">
            현재 열에 없는 값 {{ node.orphanKeys.length }}개 남음: <span class="mono">{{ node.orphanKeys.join(' · ') }}</span>
        </p>
    </div>

    <!-- 선택지로 쓰이는 텍스트 노드 -->
    <div v-else-if="node.type === 'plain_text'" class="sv2-nd" :class="{sel: isSel, struct: showStruct, mk: !!mk(node)}" :style="mkStyle(node)" @click.stop="$emit('pick', node.id)">
        <span class="chip">{{ node.type }}</span>
        <div class="opt">
            <span class="ring"></span>
            <span class="txt">{{ node.label }}</span>
            <span v-if="mk(node)" class="dots inline"><span v-for="m in mk(node)" :key="m.kind + m.label" :class="m.kind === 'evidence' ? 'clip' : 'dot'" :style="{'--c': m.c, '--ct': m.ct}" :title="(m.kind === 'evidence' ? '증빙 위치 · 평가요소 ' : '조건 ') + m.label"><AppIcon v-if="m.kind === 'evidence'" name="paperclip"/>{{ m.label }}</span></span>
        </div>
        <div v-if="node.children.length" class="opt-children">
            <sv2-node v-for="k in node.children" :key="k.id" :node="k" :sel="sel" :marks="marks"
                      :show-struct="showStruct" @pick="$emit('pick', $event)"
                      @add-column="$emit('add-column', $event)"
                      @move-column="$emit('move-column', $event)"
                      @add-row="$emit('add-row', $event)" />
        </div>
    </div>

    <!-- 입력 계열 -->
    <div v-else-if="isInput" class="sv2-nd" :class="{sel: isSel, struct: showStruct, mk: !!mk(node)}" :style="mkStyle(node)"
         @click.stop="$emit('pick', node.id)">
        <span class="chip">{{ node.type }}</span>
        <span v-if="mk(node)" class="dots"><span v-for="m in mk(node)" :key="m.kind + m.label" :class="m.kind === 'evidence' ? 'clip' : 'dot'" :style="{'--c': m.c, '--ct': m.ct}" :title="(m.kind === 'evidence' ? '증빙 위치 · 평가요소 ' : '조건 ') + m.label"><AppIcon v-if="m.kind === 'evidence'" name="paperclip"/>{{ m.label }}</span></span>
        <div class="pad">
            <span class="inp" :class="{req: node.required}">{{ node.placeholder || node.label || '' }}</span>
        </div>
    </div>

    <!-- 안내문 -->
    <div v-else-if="node.type === 'guide_text' || node.type === 'guide_text2'" class="sv2-nd" :class="{sel: isSel, struct: showStruct}" @click.stop="$emit('pick', node.id)">
        <span class="chip">{{ node.type }}</span>
        <div class="guide">{{ node.label }}</div>
    </div>

    <!-- 선택 컨테이너(radio/checkbox/dropdown) 및 그 밖 -->
    <div v-else class="sv2-nd" :class="{sel: isSel, struct: showStruct, mk: !!mk(node)}" :style="mkStyle(node)"
         @click.stop="$emit('pick', node.id)">
        <span class="chip">{{ node.type }}</span>
        <div class="pad head" :class="{mkhead: !!mk(node)}">
            <span>{{ node.label || ko(node.type) }}</span>
            <span v-if="mk(node)" class="dots"><span v-for="m in mk(node)" :key="m.kind + m.label" :class="m.kind === 'evidence' ? 'clip' : 'dot'" :style="{'--c': m.c, '--ct': m.ct}" :title="(m.kind === 'evidence' ? '증빙 위치 · 평가요소 ' : '조건 ') + m.label"><AppIcon v-if="m.kind === 'evidence'" name="paperclip"/>{{ m.label }}</span></span>
        </div>
        <sv2-node v-for="k in node.children" :key="k.id" :node="k" :sel="sel" :marks="marks"
                  :show-struct="showStruct" @pick="$emit('pick', $event)"
                      @add-column="$emit('add-column', $event)"
                      @move-column="$emit('move-column', $event)"
                      @add-row="$emit('add-row', $event)" />
        <p v-if="!node.children.length" class="pad muted">자식 노드가 없습니다.</p>
    </div>
</template>

<script>
import {nodeMeta} from '@core_utils/subComponent/structure-tree.js';

export default {
    name: 'Sv2Node',           // 자기 참조에 필요하다
    props: {
        node: {type: Object, required: true},
        sel: {type: String, default: null},
        // 구조 보기 모드. scoped 스타일로는 부모의 클래스를 자식이 볼 수 없어 prop 으로 내린다.
        showStruct: {type: Boolean, default: false},
        // 채점 표시: id → [{label, kind:'target'|'evidence', c, ct}]. 색은 평가요소 순번에서 계산돼 내려온다.
        marks: {type: Object, default: () => ({})}
    },
    data() { return {dragFrom: -1, dragOver: -1}; },
    computed: {
        isSel() { return this.node.id === this.sel; },
        invisible() { return this.node.type === 'plain_text' && !String(this.node.label || '').trim() && !this.node.children.length; },
        isInput() { return ['input_text', 'input_number', 'textbox', 'calc', 'file_URL2'].indexOf(this.node.type) >= 0; }
    },
    methods: {
        ko(t) { return t ? nodeMeta(t).ko : '빈 칸'; },
        onDragStart(i, e) {
            this.dragFrom = i;
            if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }
        },
        onDrop(tableId, to) {
            const from = this.dragFrom;
            this.dragFrom = -1; this.dragOver = -1;
            if (from < 0 || from === to) return;
            this.$emit('move-column', {tableId, from, to});
        },
        content(cell) { return (cell.children && cell.children[0]) || null; },
        mk(node) { const m = node && this.marks[node.id]; return m && m.length ? m : null; },
        // 테두리는 첫 조건 색 — 동률 처리일 뿐이다. 누가 가리키는지는 번호 찍힌 점이 말한다.
        mkStyle(node) { const m = this.mk(node); return m ? {'--c': m[0].c, '--ct': m[0].ct} : null; },
        contentType(cell) { const c = this.content(cell); return c ? c.type : null; },
        cellSel(cell) {
            const c = this.content(cell);
            return cell.id === this.sel || (c && c.id === this.sel);
        }
    }
};
</script>

<style scoped>
.sv2-nd{position:relative;border:1px solid transparent;border-radius:5px;margin:.1rem 0}
.sv2-nd:hover{border-color:#dde2e9}
.sv2-nd.sel{border-color:#2f6b8f;box-shadow:0 0 0 1px #2f6b8f}
.chip{position:absolute;top:-.6rem;left:.5rem;font-family:Consolas,monospace;font-size:.62rem;
  background:#2f6b8f;color:#fff;border-radius:3px;padding:0 .3rem;opacity:0;pointer-events:none;z-index:2}
.sv2-nd:hover > .chip,.sv2-nd.sel > .chip{opacity:1}

.opt{display:flex;gap:.5rem;align-items:flex-start;padding:.35rem .5rem}
.opt .ring{width:.85rem;height:.85rem;border:1.5px solid #8b95a3;border-radius:50%;flex:none;margin-top:.28rem}
.opt .txt{flex:1;font-size:.9rem}
.opt-children{margin:.3rem 0 .7rem 1.4rem;padding-left:.55rem;border-left:2px solid #eaeef3}
.pad{padding:.35rem .5rem} .pad.head{font-weight:600;font-size:.9rem}
.muted{color:#8b95a3;font-size:.82rem}
.guide{padding:.5rem .6rem;background:#f5f7fa;border-radius:5px;font-size:.85rem;white-space:pre-line;color:#525c6b}

.tw{overflow-x:auto;padding:.2rem}
.pt{width:100%;border-collapse:collapse;font-size:.85rem;table-layout:fixed}
.pt th,.pt td{border:1px solid #dde2e9;padding:.3rem .45rem;text-align:left;vertical-align:top;
  position:relative;overflow-wrap:anywhere;word-break:break-word}
.pt th{background:#f5f7fa;font-weight:600;font-size:.79rem}
.pt th.colh{cursor:grab;user-select:none}
.pt th.colh .grip{opacity:0;color:#8b95a3;font-size:.7rem;margin-right:.2rem;transition:opacity .1s}
.pt th.colh:hover .grip{opacity:.7}
.pt th.colh.drop{box-shadow:inset 3px 0 0 #2f6b8f}
.pt th.colh:hover{color:#2f6b8f;outline:1px solid #dde2e9;outline-offset:-1px}
.pt th.colh.sel{outline:2px solid #2f6b8f;outline-offset:-2px;background:#e6f0f6}
.pt th.filler,.pt td.filler{border:0;background:transparent;width:.5rem}
.pt th.colh.add{width:1.8rem;text-align:center;color:#8b95a3;font-weight:400;cursor:pointer}
.pt th.colh.add:hover{color:#2f6b8f;background:#e6f0f6}
/* 행 추가는 열 추가와 같은 기호·같은 '표의 끝' 자리에 둔다 */
.pt td.addrow{text-align:center;color:#8b95a3;cursor:pointer;background:#fbfcfd;padding:.15rem}
.pt td.addrow:hover{color:#2f6b8f;background:#e6f0f6}
.pt td.cell{cursor:pointer}
.pt td.cell:hover{outline:1px solid #dde2e9;outline-offset:-1px}
.pt td.cell.sel{outline:2px solid #2f6b8f;outline-offset:-2px;background:#e6f0f6}
.pt td.cell.mk{outline:2px solid var(--c);outline-offset:-2px;background:var(--ct)}
.pt .disp{color:#141921}
.pt .inp{display:block;border:1px solid #dde2e9;border-radius:4px;padding:.18rem .3rem;
  background:#f5f7fa;color:#8b95a3;font-size:.82rem;min-height:1.5rem}
.pt .inp.empty{opacity:.6}
.pt .dd{position:relative;padding-right:1.2rem;color:#141921}
.pt .dd::before{content:'▾';position:absolute;right:.35rem;top:.12rem;color:#8b95a3;font-size:.7rem}
.req::after{content:' *';color:#a33228;font-weight:700}

.orph{margin:.3rem .3rem 0;font-size:.78rem;color:#8a6212}
.mono{font-family:Consolas,monospace}

/* 채점 조건 표시: 테두리는 조건 색, 점은 가리키는 조건마다 하나씩(번호 포함) */
.sv2-nd.mk{border-color:var(--c)}
.pad.head{display:flex;align-items:center;gap:.4rem}
.pad.head.mkhead{background:var(--ct);border-radius:4px 4px 0 0}
.dots{display:inline-flex;gap:.2rem;align-items:center;vertical-align:middle}
.pt td.cell .dots{position:absolute;top:.15rem;right:.2rem;z-index:1}
.sv2-nd > .dots{position:absolute;top:.35rem;right:.5rem;z-index:1}
.dot{min-width:.8rem;height:.8rem;padding:0 .15rem;border-radius:999px;background:var(--c);box-shadow:0 0 0 1.5px #fff;
  display:inline-flex;align-items:center;justify-content:center;font-family:Consolas,monospace;
  font-size:.5rem;font-weight:700;color:#fff;line-height:1}
/* 증빙 위치: 클립 모양 알약. 수식 대상(점)과 한눈에 구분된다 */
.clip{display:inline-flex;align-items:center;gap:.1rem;height:.95rem;padding:0 .3rem;border-radius:3px;
  background:var(--ct);color:var(--c);border:1px solid var(--c);font-family:Consolas,monospace;font-size:.58rem;font-weight:700;line-height:1;white-space:nowrap}
.dots.inline{position:static;margin-left:.3rem}
.pt tr.mkrow td.cell{box-shadow:inset 3px 0 0 var(--c)}
.pt td.filler .rowdots{position:absolute;right:auto;left:.1rem;top:.25rem}
.pt td.filler{position:relative;width:2.2rem}

.sv2-phantom{display:flex;align-items:center;gap:.4rem;border:1px dashed #8b95a3;border-radius:5px;
  padding:.28rem .5rem;margin:.25rem 0;font-size:.79rem;color:#8b95a3;cursor:pointer}
.sv2-phantom:hover,.sv2-phantom.sel{border-color:#2f6b8f;color:#2f6b8f}

/* 구조 보기 모드: 경계와 유형 칩을 항상 노출 */
.sv2-nd.struct{border-color:#dde2e9}
.sv2-nd.struct > .chip{opacity:.85}
.sv2-nd.struct .pt td.cell::after,
.sv2-nd.struct .pt th.colh::after{content:attr(data-mode);position:absolute;right:.25rem;bottom:.02rem;
  font-family:Consolas,monospace;font-size:.58rem;color:#8b95a3;font-weight:400}
</style>
